<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Sales',
  },
})

const ability = useAbility()
const { formatRupiah, formatDate } = useFormat()

// ── State ──────────────────────────────────────────────────────────────
const orders = ref([])
const totalItems = ref(0)
const options = ref({ page: 1, itemsPerPage: 20, sortBy: [] })
const statusFilter = ref(null)
const dateFrom = ref(null)
const dateTo = ref(null)
const isLoading = ref(false)

const voidDialog = ref(false)
const selectedOrder = ref(null)

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Selesai', value: 'completed' },
  { title: 'Sudah Dibayar', value: 'paid' },
  { title: 'Void', value: 'void' },
  { title: 'Pending', value: 'pending' },
]

// Sinkron dengan orderStatusMeta() di sales/orders/[id].vue
const orderStatusMeta = status => {
  switch (status) {
    case 'completed': return { label: 'Selesai', color: 'success' }
    case 'paid': return { label: 'Sudah Dibayar', color: 'info' }
    case 'void': return { label: 'Void', color: 'error' }
    default: return { label: 'Pending', color: 'warning' }
  }
}

const headers = [
  { title: 'No. Order', key: 'order_number' },
  { title: 'Pelanggan', key: 'customer_name' },
  { title: 'Kasir', key: 'creator.name' },
  { title: 'Total', key: 'total' },
  { title: 'Status', key: 'status' },
  { title: 'Waktu', key: 'created_at' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '100px' },
]

// ── Fetch ──────────────────────────────────────────────────────────────
const fetchOrders = async () => {
  isLoading.value = true
  try {
    const response = await useApi('/api/v1/orders', {
      query: {
        page: options.value.page,
        per_page: options.value.itemsPerPage,
        status: statusFilter.value || undefined,
        date_from: dateFrom.value || undefined,
        date_to: dateTo.value || undefined,
      },
    }).get()
    const data = response?.data ?? response

    orders.value = data?.data ?? []
    totalItems.value = data?.meta?.total ?? 0
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchOrders)
watch(options, fetchOrders, { deep: true })
watch([statusFilter, dateFrom, dateTo], fetchOrders)

const resetFilters = () => {
  statusFilter.value = null
  dateFrom.value = null
  dateTo.value = null
}

// ── Void ───────────────────────────────────────────────────────────────
const openVoidDialog = order => {
  selectedOrder.value = order
  voidDialog.value = true
}

const handleVoided = () => {
  fetchOrders()
}
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Daftar Order</h4>
        <p class="text-body-2 text-medium-emphasis">
          Riwayat semua transaksi penjualan
        </p>
      </VCol>
    </VRow>

    <VCard>
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VSelect
          v-model="statusFilter"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          label="Status"
          density="compact"
          hide-details
          style="max-width: 180px"
        />

        <VTextField
          v-model="dateFrom"
          type="date"
          label="Dari Tanggal"
          density="compact"
          hide-details
          style="max-width: 170px"
        />

        <VTextField
          v-model="dateTo"
          type="date"
          label="Sampai Tanggal"
          density="compact"
          hide-details
          style="max-width: 170px"
        />

        <VBtn variant="text" size="small" @click="resetFilters">
          Reset Filter
        </VBtn>

        <VSpacer />

        <VBtn
          v-if="ability.can('create', 'Sales')"
          :to="{ name: 'pos' }"
          color="primary"
          prepend-icon="ri-shopping-cart-line"
        >
          Buka POS Kasir
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTable
        v-model:options="options"
        :headers="headers"
        :items="orders"
        :loading="isLoading"
        :items-length="totalItems"
        hover
      >
        <template #item.order_number="{ item }">
          <RouterLink
            :to="{ name: 'sales-orders-id', params: { id: item.id } }"
            class="text-primary font-weight-medium"
          >
            {{ item.order_number }}
          </RouterLink>
        </template>

        <template #item.customer_name="{ item }">
          {{ item.customer_name ?? '-' }}
        </template>

        <template #item.creator.name="{ item }">
          {{ item.creator?.name ?? '-' }}
        </template>

        <template #item.total="{ item }">
          <span class="font-weight-medium">{{ formatRupiah(item.total) }}</span>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="orderStatusMeta(item.status).color"
            size="small"
            variant="tonal"
          >
            {{ orderStatusMeta(item.status).label }}
          </VChip>
        </template>

        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at, 'DD MMM YYYY HH:mm') }}
        </template>

        <template #item.actions="{ item }">
          <VBtn
            icon
            size="small"
            variant="text"
            :to="{ name: 'sales-orders-id', params: { id: item.id } }"
          >
            <VIcon icon="ri-eye-line" />
          </VBtn>
          <VBtn
            v-if="item.status === 'completed' && ability.can('delete', 'Sales')"
            icon
            size="small"
            variant="text"
            color="error"
            @click="openVoidDialog(item)"
          >
            <VIcon icon="ri-close-circle-line" />
            <VTooltip activator="parent">Void Order</VTooltip>
          </VBtn>
        </template>

        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-receipt-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-4">Belum ada order</p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VoidOrderDialog
      v-model="voidDialog"
      :order="selectedOrder"
      @voided="handleVoided"
    />
  </div>
</template>
